import { apiErrorCode } from '../../utils/errors';
// Post-login intent replay.
//
// During the guest flow we sometimes stash an action the user wanted
// to perform (under sessionStorage.post_login_intent) and bounce them
// to register or login. After they authenticate, this handler reads
// the stash and dispatches the action so the user lands where they
// expected — instead of dumped at the dashboard with their work
// apparently lost.
//
// Supported intents:
//   - claim_guest_project:  transfer guest project + invites + reviews
//                           to the new account, then go to the project.
//   - send_invite:          claim the project, then create the stashed
//                           invite, then go to the project's reviews.
//   - request_expert_review:claim the project, then create the stashed
//                           review request, then go to the report.
//   - claim_invite:         just navigate to the reviews page so the
//                           user can manage their imported invites.
//
// We run ONCE per auth transition: when `isAuthenticated` flips from
// false → true. The intent is cleared from sessionStorage BEFORE the
// dispatched action runs so a failure doesn't loop forever.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { useToast } from '../../context/ToastContext.jsx';
import RequirementsV2API from '../../api/requirementsV2.js';
import ReviewStore from '../../modules/requirements/report/reviewStore.js';

const INTENT_KEY = 'post_login_intent';

const readIntent = () => {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearIntent = () => {
  try { sessionStorage.removeItem(INTENT_KEY); } catch { /* ignore */ }
};

const readGuestToken = () => {
  try { return localStorage.getItem('guestToken') || ''; } catch { return ''; }
};

const clearGuestToken = () => {
  // Once a guest project is claimed, the guest session is no longer
  // useful — the user is now a real account holder. Removing the
  // token also prevents the requirementsV2 client from re-using it.
  try { localStorage.removeItem('guestToken'); } catch { /* ignore */ }
};

export default function PostLoginIntentHandler() {
  const { isAuthenticated, loading } = useAuthContext();
  const { showToast } = useToast() || {};
  const navigate = useNavigate();
  const handledRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) return;
    if (handledRef.current) return;

    const intent = readIntent();
    if (!intent || typeof intent !== 'object' || !intent.kind) return;

    handledRef.current = true;

    // Shared step: ensure the guest project is claimed BEFORE we try
    // any project-scoped action (invite, review request). Three of
    // the four intents below need to claim first; this dedupes the
    // logic. Always-best-effort — a claim failure doesn't block the
    // subsequent action because the user might already own the
    // project (e.g. claimed earlier in the session and re-logging in).
    // The token is cleared only on success, so a claim that genuinely failed
    // stays retryable next time the user lands here. The API layer already
    // retries the transient cases; a failure that reaches this catch is real,
    // and swallowing it silently is how work disappears at the exact moment
    // someone decided to trust us — so it is surfaced.
    const ensureClaimed = async (projectId) => {
      if (!projectId) return;
      const guestToken = readGuestToken();
      if (!guestToken) return;
      try {
        await RequirementsV2API.claim(projectId, guestToken);
        clearGuestToken();
      } catch (err) {
        // An already-claimed project comes back as a no-op success, not an
        // error, so a mismatch here means this really is someone else's.
        const code = apiErrorCode(err);
        if (code === 'V2.CLAIM_OWNERSHIP_MISMATCH') { clearGuestToken(); return; }
        showToast?.({
          type: 'warning',
          message: 'We could not finish linking your earlier project. Open it from My projects to try again.'
        });
      }
    };

    // Shared fallback: every project-scoped intent below needs a
    // projectId. If we don't have one (stashed intent got truncated,
    // bookmarked from a stale link, etc.) bail to projects and tell
    // the user — beats stranding them on the dashboard wondering why
    // nothing happened.
    const missingProjectFallback = () => {
      navigate('/projects', { replace: true });
      showToast?.({
        type: 'info',
        message: 'Welcome — open your project from the list to continue where you left off.'
      });
    };

    const run = async () => {
      // Defensive clear-first: if the call below crashes / loops, we
      // don't want to retry forever on every render.
      clearIntent();

      switch (intent.kind) {
        case 'claim_guest_project': {
          const projectId = intent.projectId;
          if (!projectId) { missingProjectFallback(); return; }
          await ensureClaimed(projectId);
          if (intent.returnTo) {
            navigate(intent.returnTo, { replace: true });
          } else {
            navigate(`/requirements/v2/${projectId}`, { replace: true });
          }
          return;
        }

        case 'send_invite': {
          // Claim the guest project so the invite shows up under the
          // user's account. Then re-submit the invite from their
          // authenticated session — the backend stamps it with the
          // new ownerId.
          const projectId = intent.projectId;
          if (!projectId) { missingProjectFallback(); return; }
          await ensureClaimed(projectId);
          const invitee = intent.invitee || {};
          try {
            await ReviewStore.createInvite({
              projectId,
              moksyUsername: invitee.moksyUsername || null,
              name: invitee.name || null,
              email: invitee.email || null,
              note: intent.note || '',
              sections: Array.isArray(intent.sections) ? intent.sections : [],
              requestedBy: intent.requestedBy || null
            });
          } catch (_) {
            // If the guest already sent the invite before bouncing
            // through register, the backend will see an active invite
            // for the same target and may reject as a duplicate (cap).
            // That's fine — the invite is already there.
          }
          navigate(`/requirements/v2/${projectId}/reviews`, { replace: true });
          return;
        }

        case 'request_expert_review': {
          const projectId = intent.projectId;
          if (!projectId) { missingProjectFallback(); return; }
          await ensureClaimed(projectId);
          // Tier-aware resume:
          //   free        → create the review directly; community tier
          //                 has no payment / entitlement step.
          //   urgent      → MUST land on the modal's needsPayment step
          //                 so the user confirms the MOX debit (and tops
          //                 up if their wallet is short). Auto-creating
          //                 here would silently fail with INSUFFICIENT
          //                 _FUNDS or, worse, send the user to the
          //                 report with no review made.
          //   subscription→ MUST land on the modal's plan picker if the
          //                 user is on free plan; the modal handles
          //                 plan selection + Stripe Checkout. Direct
          //                 create returns SUBSCRIPTION_REQUIRED.
          // For urgent/subscription we re-stash the intent and bounce
          // to the project page. The page-level resume effect
          // (RequirementsV2.jsx) reads the intent and pops the modal
          // open with the right tier pre-selected, dropping the user
          // straight into the correct step.
          const tier = String(intent.tier || 'subscription').toLowerCase();
          if (tier === 'urgent' || tier === 'subscription') {
            try {
              sessionStorage.setItem('post_login_intent', JSON.stringify({
                kind: 'request_expert_review',
                projectId,
                tier,
                returnTo: intent.returnTo || `/requirements/v2/${projectId}`
              }));
            } catch { /* ignore */ }
            navigate(`/requirements/v2/${projectId}`, { replace: true });
            return;
          }
          // Free / community tier — no extra confirmation needed.
          try {
            await ReviewStore.create({
              projectId,
              tier,
              scope: 'whole',
              sections: []
            });
          } catch (_) { /* duplicate or quota — user lands on the report and sees status */ }
          navigate(`/requirements/v2/${projectId}/report`, { replace: true });
          return;
        }

        case 'claim_invite': {
          // The user registered to manage an invite that someone sent
          // them (or that they themselves sent as a guest). No
          // backend action — invites already migrate during the
          // claimProject sweep. Just land them on the right surface.
          const projectId = intent.projectId;
          if (projectId) {
            await ensureClaimed(projectId);
            navigate(`/requirements/v2/${projectId}/reviews`, { replace: true });
            showToast?.({
              type: 'success',
              message: 'Welcome — your invite has been linked to your new account.'
            });
          } else {
            // No project context — the invite link was opened directly
            // without a projectId stashed in the intent (older link,
            // shared via channel that stripped the query, etc.). Land
            // on the My reviews surface where the user can see ALL
            // invites bound to their email, and tell them what
            // happened so they don't think their work was lost.
            navigate('/reviews', { replace: true });
            showToast?.({
              type: 'info',
              message: 'Welcome — open My reviews to find the invite you came in for.'
            });
          }
          return;
        }

        // The switch is intentionally explicit (not a registry) so
        // each new intent is a code-review touchpoint.
        default:
          return;
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading]);

  return null;
}
